import { useEffect, useState, type KeyboardEvent, type ReactNode } from "react";
import { UploadCloud, ChevronLeft, Check, RefreshCw } from "lucide-react";
import { toast } from "../../../lib/toast";
import { useSetup } from "../SetupContext";
import { apiRequest } from "../../../../services/apiClient";

const TAX_OPTIONS = [
  { id: "UNSPSC" as const, title: "UNSPSC", desc: "Three-level hierarchy from the supplied UNSPSC taxonomy" },
  { id: "Custom" as const, title: "Custom Taxonomy", desc: "Upload a client specific classification hierarchy" }
];

interface TaxonomyNode {
  id: string;
  label: string;
  level: number;
  children: TaxonomyNode[];
}

export function SubStep2({ onNext, onBack, onCancel }: { onNext: () => void; onBack: () => void; onCancel: () => void }) {
  const { form, setField } = useSetup();
  const [nodes, setNodes] = useState<TaxonomyNode[]>([]);
  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const [expandedParents, setExpandedParents] = useState<Set<string>>(new Set());
  const hasTaxonomy = Boolean(form.taxonomy);

  useEffect(() => {
    if (!hasTaxonomy) return;
    const controller = new AbortController();
    setStatus("loading");
    setNodes([]);
    setExpandedParents(new Set());
    setFocusedId(null);
    apiRequest("/api/taxonomies/unspsc", { signal: controller.signal })
      .then((response: { data: TaxonomyNode[] }) => {
        if (controller.signal.aborted) return;
        setNodes(response.data);
        setFocusedId(response.data[0]?.id ?? null);
        setStatus("success");
      })
      .catch(() => {
        if (!controller.signal.aborted) setStatus("error");
      });
    return () => controller.abort();
  }, [hasTaxonomy, loadAttempt]);

  const toggleParent = (id: string) => {
    setExpandedParents((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleTreeKeyDown = (event: KeyboardEvent<HTMLDivElement>, node: TaxonomyNode) => {
    if (event.target !== event.currentTarget) return;
    const item = event.currentTarget;
    const items = Array.from(item.closest('[role="tree"]')!.querySelectorAll<HTMLDivElement>('[role="treeitem"]'));
    const index = items.indexOf(item);
    const expanded = expandedParents.has(node.id);
    switch (event.key) {
      case "Enter":
      case " ":
        if (node.children.length) toggleParent(node.id);
        break;
      case "ArrowRight":
        if (node.children.length && !expanded) toggleParent(node.id);
        else item.querySelector<HTMLDivElement>('[role="treeitem"]')?.focus();
        break;
      case "ArrowLeft":
        if (node.children.length && expanded) toggleParent(node.id);
        else item.parentElement?.closest<HTMLDivElement>('[role="treeitem"]')?.focus();
        break;
      case "ArrowDown": items[index + 1]?.focus(); break;
      case "ArrowUp": items[index - 1]?.focus(); break;
      case "Home": items[0]?.focus(); break;
      case "End": items[items.length - 1]?.focus(); break;
      default: return;
    }
    event.preventDefault();
    event.stopPropagation();
  };

  const renderNodes = (branches: TaxonomyNode[]): ReactNode => branches.map((node) => {
    const isParent = node.children.length > 0;
    const isExpanded = expandedParents.has(node.id);
    return (
      <div
        key={node.id}
        role="treeitem"
        aria-label={node.label}
        aria-level={node.level}
        aria-expanded={isParent ? isExpanded : undefined}
        tabIndex={focusedId === node.id ? 0 : -1}
        onFocus={(event) => { if (event.target === event.currentTarget) setFocusedId(node.id); }}
        onKeyDown={(event) => handleTreeKeyDown(event, node)}
      >
        <div
          className="taxonomy-tree__row"
          style={{ paddingLeft: 16 + (node.level - 1) * 16 }}
          onClick={(event) => {
            event.currentTarget.parentElement?.focus();
            if (isParent) toggleParent(node.id);
          }}
        >
          <div className="taxonomy-tree__row-left">
            <span className="taxonomy-tree__toggle" aria-hidden="true">
              {isParent ? (isExpanded ? "▼" : "▶") : ""}
            </span>
            <span>{node.label}</span>
          </div>
          {isParent && (
            <span className="taxonomy-tree__row-count">
              {node.children.length} {node.children.length === 1 ? "category" : "categories"}
            </span>
          )}
        </div>
        {isParent && isExpanded && <div role="group">{renderNodes(node.children)}</div>}
      </div>
    );
  });

  return (
    <div>
      <div className="setup-form-header">
        <div className="setup-form-title">Step 2 · Taxonomy configuration</div>
        <div className="setup-form-desc">Choose a standard taxonomy or your client's own.</div>
      </div>

      <div className="taxonomy-options" role="radiogroup" aria-label="Taxonomy type">
        {TAX_OPTIONS.map((opt) => (
          <div
            key={opt.id}
            className={`taxonomy-option${form.taxonomy === opt.id ? " is-selected" : ""}`}
            role="radio"
            aria-checked={form.taxonomy === opt.id}
            tabIndex={0}
            onClick={() => setField("taxonomy", opt.id)}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setField("taxonomy", opt.id); }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 4 }}>
              <div className="taxonomy-option__title">{opt.title}</div>
              {form.taxonomy === opt.id && (
                <div style={{ width: 18, height: 18, borderRadius: "50%", background: "#6b3fa0", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <Check size={11} color="#fff" />
                </div>
              )}
            </div>
            <div className="taxonomy-option__desc">{opt.desc}</div>
          </div>
        ))}
      </div>

      <div className="taxonomy-or" role="separator" aria-label="or">
        <span className="taxonomy-or__text">or</span>
      </div>

      <div
        className="file-upload-zone"
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          const file = e.dataTransfer.files[0];
          if (file) { setField("uploadedFile", file.name); toast.success(`Uploaded: ${file.name}`); }
        }}
        style={{ textAlign: "center" }}
      >
        <UploadCloud size={24} style={{ margin: "0 auto 8px", color: "var(--text-muted)" }} aria-hidden="true" />
        <div className="file-upload-zone__label">
          {form.uploadedFile ? `✓ ${form.uploadedFile}` : "Upload Taxonomy File"}
        </div>
        <div className="file-upload-zone__hint">CSV or Excel. Drag & drop or click to browse.</div>
        <input
          type="file"
          accept=".csv,.xlsx,.xls"
          style={{ position: "absolute", inset: 0, opacity: 0, cursor: "pointer", width: "100%", height: "100%" }}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) { setField("uploadedFile", file.name); toast.success(`Uploaded: ${file.name}`); }
          }}
          aria-label="Upload taxonomy file"
        />
      </div>

      {form.taxonomy && (
        <>
          <div className="taxonomy-preview-label">Taxonomy Preview — {form.taxonomy}</div>
          {status === "loading" && <div role="status">Loading taxonomy...</div>}
          {status === "error" && (
            <div role="alert">
              <p>Unable to load the taxonomy.</p>
              <button type="button" className="btn btn--outline" onClick={() => setLoadAttempt((attempt) => attempt + 1)}>
                <RefreshCw size={14} /> Retry
              </button>
            </div>
          )}
          {status === "success" && (nodes.length ? (
            <div className="taxonomy-tree" role="tree" aria-label={`${form.taxonomy} taxonomy`}>
              {renderNodes(nodes)}
            </div>
          ) : <div role="status">No taxonomy categories are available.</div>)}
        </>
      )}

      <div className="wizard-action-bar" style={{ marginTop: 24 }}>
        <button type="button" className="action-bar__cancel btn btn--outline" onClick={onCancel}>
          Cancel
        </button>
        <button type="button" className="btn btn--outline m-left-auto" onClick={onBack}>
          <ChevronLeft size={14} /> Back
        </button>
        <button type="button" className="btn btn--primary" onClick={onNext} disabled={!hasTaxonomy} style={{ opacity: hasTaxonomy ? 1 : 0.5 }}>
          Continue
        </button>
      </div>
    </div>
  );
}
